import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ConfirmationDialog, FormField, RadixHoverDropdown } from '@/components/custom/erp';
import SaveTemplateDialog from './form-dialog';
import DetailDialog from './detail-dialog';
import { useForm } from './hooks/useForm';
import { QUERY_KEYS } from '@/constants';
import { Label } from '@/components/ui';
import { useCRUD } from '@/hooks';

interface ReportTemplateDropdownProps {
  label?: string;
  className?: string;
  labelClassName?: string;
  selectClassName?: string;
  name?: string;
}

export const ReportTemplateDropdown = () => {
  const { watch, setValue } = useFormContext();
  const selectedTemplateFormUuid = watch('id_maubc');
  const { data, addItem, updateItem, deleteItem, refreshData } = useCRUD({
    endpoint: QUERY_KEYS.MAU_BAO_CAO_KET_QUA_HOAT_DONG_KINH_DOANH
  });
  const templateOptions = useMemo(
    () =>
      data?.map((item: any) => ({
        label: item.ten_maubc,
        value: item.uuid
      })),
    [data]
  );

  useEffect(() => {
    if (!selectedTemplateFormUuid && templateOptions?.length > 0) {
      setValue('id_maubc', templateOptions[0].value);
    }
  }, [selectedTemplateFormUuid, setValue, templateOptions]);

  const selectedTemplateObj = useMemo(
    () => data?.find((item: any) => item.uuid === selectedTemplateFormUuid) || {},
    [data, selectedTemplateFormUuid]
  );
  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    showDetail,
    handleShowDetail,
    handleCloseForm,
    handleCloseDelete,
    handleCloseDetail,
    handleAddClick,
    handleEditClick,
    handleDeleteClick
  } = useForm({
    selectedObj: selectedTemplateObj
  });

  const [selectedTemplateUuid, setSelectedTemplateUuid] = useState<string>('');

  useEffect(() => {
    if (selectedTemplateFormUuid) {
      setSelectedTemplateUuid(selectedTemplateFormUuid);
    }
  }, [selectedTemplateFormUuid]);

  const handleSubmit = async (data: any) => {
    if (formMode === 'add') {
      const result = await addItem(data);
      if (result?.uuid) {
        setSelectedTemplateUuid(result.uuid);
        setValue('id_maubc', result.uuid);
      }
    } else {
      await updateItem(selectedObj?.uuid, data);
      setSelectedTemplateUuid(selectedObj?.uuid || selectedTemplateFormUuid || '');
    }
    refreshData();
    handleShowDetail();
    handleCloseForm();
  };

  return (
    <div className='flex items-center'>
      <Label className='w-32 min-w-32'>Chọn báo cáo</Label>
      <div className='w-[400px]'>
        <FormField type='select' name='id_maubc' options={templateOptions} className='w-full' />
      </div>
      <div className='ml-2 h-9 w-9 flex-shrink-0'>
        {/* Fixed size container */}
        <RadixHoverDropdown
          iconNumber='...'
          items={[
            {
              value: 'save',
              label: 'Tạo mẫu mới',
              icon: 7,
              onClick: () => {
                handleAddClick();
              }
            },
            {
              value: 'edit',
              label: 'Sửa mẫu đang chọn',
              icon: 9,
              onClick: () => handleEditClick()
            },
            {
              value: 'delete',
              label: 'Xóa mẫu đang chọn',
              icon: 8,
              onClick: () => handleDeleteClick()
            }
          ]}
        />
      </div>

      {showForm && (
        <SaveTemplateDialog
          open={showForm}
          onClose={handleCloseForm}
          onSave={handleSubmit}
          mode={formMode}
          initialData={formMode === 'edit' ? selectedObj : undefined}
        />
      )}
      {showDetail && <DetailDialog onClose={handleCloseDetail} selectedTemplateUuid={selectedTemplateUuid} />}
      {showDelete && (
        <ConfirmationDialog
          open={showDelete}
          onClose={handleCloseDelete}
          onConfirm={async () => {
            await deleteItem(selectedObj?.uuid);
            await refreshData();
            handleCloseDelete();
            if (data?.length > 0) {
              setValue('id_maubc', data?.[0]?.uuid);
            }
          }}
          message='Bạn có chắc chắn xoá mẫu báo cáo này?'
        />
      )}
    </div>
  );
};
